import { QUIZ_IMAGE_ANCHORS } from "./quizImageAnchors";

export type QuizStepKey = "priorities" | "constraints";

export type StepImageEntry = { src: string; alt: string; objectPosition?: string };

/**
 * Crop anchor for the fixed 3:2 photo box (Option D — see the design
 * handoff at Extra Files/Claude Design - Handoff Files/
 * design_handoff_quiz_photo_panel_option_d/README.md). Centre-fallback: an
 * untagged or newly added image is always safe, just unconsidered — run
 * scripts/audit-quiz-image-ratios.mjs to regenerate QUIZ_IMAGE_ANCHORS and
 * flag it.
 */
function resolveAnchor(key: string): string {
  return QUIZ_IMAGE_ANCHORS[key] ?? "50% 50%";
}

/**
 * Image pools per quiz step.
 *
 * To add images: drop files in public/quiz/ named <stepKey>-<n>.jpg
 * (e.g. priorities-1.jpg, priorities-2.jpg) and add each entry here, then
 * re-run scripts/audit-quiz-image-ratios.mjs so the new image gets a
 * QUIZ_IMAGE_ANCHORS entry. Next.js cannot enumerate public/ at runtime, so
 * only listed files are shown.
 *
 * The pool is rendered by QuizStepImageCarousel.tsx, which auto-cycles
 * through every entry (crossfade, same mechanism as the marketing hero
 * carousel) rather than showing a single fixed pick.
 */
export const STEP_IMAGES: Record<QuizStepKey, StepImageEntry[]> = {
  priorities: Array.from({ length: 40 }, (_, i) => {
    const key = `priorities-${i + 1}`;
    return { src: `/quiz/${key}.jpg`, alt: "", objectPosition: resolveAnchor(key) };
  }),
  constraints: Array.from({ length: 40 }, (_, i) => {
    const key = `constraints-${i + 1}`;
    return { src: `/quiz/${key}.jpg`, alt: "", objectPosition: resolveAnchor(key) };
  }),
};

/**
 * The ONLY place URL-affecting <Image> props may live. Anything that changes
 * the optimizer URL — quality, unoptimized, loader, overrideSrc — must go
 * here and not be added to <Image> directly in QuizStepImageCarousel.
 * preloadStepImage.ts spreads this same bag into getImageProps; if the two
 * ever diverge, preloadStepImage derives a different srcSet, React's preload
 * dedupe key (imagesrcset+imagesizes) misses, and the browser silently
 * double-fetches.
 */
export const STEP_IMAGE_URL_PROPS = {
  fill: true,
  sizes: "(max-width: 768px) 100vw, 50vw",
} as const;
